import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faChevronRight, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import logo from "../../logo.png";
import Menu from "./Menu";

const FlagIcon = ({ code }) => {
  if (code === "it") {
    return (
      <svg
        className="lang-flag-icon"
        viewBox="0 0 3 2"
        aria-hidden="true"
        focusable="false"
      >
        <rect width="1" height="2" x="0" fill="#009246" />
        <rect width="1" height="2" x="1" fill="#ffffff" />
        <rect width="1" height="2" x="2" fill="#ce2b37" />
      </svg>
    );
  }
  return (
    <svg
      className="lang-flag-icon"
      viewBox="0 0 60 30"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="60" height="30" fill="#012169" />
      <path d="M0 0 L60 30 M60 0 L0 30" stroke="#ffffff" strokeWidth="6" />
      <path d="M0 0 L60 30 M60 0 L0 30" stroke="#C8102E" strokeWidth="2" />
      <path d="M30 0 v30 M0 15 h60" stroke="#ffffff" strokeWidth="10" />
      <path d="M30 0 v30 M0 15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
};

const Navbar = ({ languages, data }) => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState(null);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [currentLang, setCurrentLang] = useState(
    (i18n.language || "it").substring(0, 2)
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const isEnglish = (i18n.language || "it").startsWith("en");

  const getSubmenuTitle = (sub) => {
    switch (sub) {
      case "who":
        return t("nav.who_we_are");
      case "pub":
        return t("nav.publications");
      case "act":
        return t("nav.activity");
      case "trasp":
        return t("nav.transparency");
      default:
        return "";
    }
  };

  useEffect(() => {
    const handleLangChanged = (lng) => {
      if (lng) {
        setCurrentLang(lng.substring(0, 2));
      }
    };
    i18n.on("languageChanged", handleLangChanged);
    return () => {
      i18n.off("languageChanged", handleLangChanged);
    };
  }, [i18n]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".nav-item.dropdown")) {
        setOpenSubmenus({});
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("menu-open-noscroll");
    } else {
      document.body.classList.remove("menu-open-noscroll");
    }
    return () => {
      document.body.classList.remove("menu-open-noscroll");
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setOpenSubmenus({});
      }
    };
    const handleResize = () => {
      if (window.innerWidth > 992 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setOpenSubmenus({});
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen((prev) => {
      if (prev) {
        setActiveMobileSubmenu(null);
      }
      return !prev;
    });
  };

  const toggleDropdown = (index) => {
    setOpenSubmenus((prev) => (prev[index] ? {} : { [index]: true }));
  };

  const closeMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveMobileSubmenu(null);
    setOpenSubmenus({});
  };

  const handleLanguageChange = (code, e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    setCurrentLang(code);
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("i18nextLng", code);
    } catch (err) {
      // ignore storage error
    }

    if (isMobileMenuOpen) {
      setTimeout(() => {
        closeMenu();
      }, 200);
    }
  };

  return (
    <header
      className={`header graded-header ${
        isMobileMenuOpen ? "mobile-menu-open" : ""
      } ${isScrolled ? "header-scrolled" : ""}`}
    >
      <div className="container">
        <nav
          className="navbar navbar-toggleable-md navbar-inverse yamm"
          id="slide-nav"
          role="navigation"
          aria-label="Navigazione principale"
        >
          <button
            className={`navbar-toggler navbar-toggler-right d-lg-none ${
              isMobileMenuOpen ? "open" : ""
            }`}
            type="button"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle navigation"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="navbar-toggler-icon">
              <span className="burger-bar bar-top"></span>
              <span className="burger-bar bar-mid"></span>
              <span className="burger-bar bar-bot"></span>
            </span>
          </button>

          <Link className="navbar-brand" to="/" onClick={closeMenu}>
            <img
              src={logo}
              alt="Nuova Orchestra da Camera Ferruccio Busoni"
              className="navbar-logo"
            />
          </Link>

          {/* Desktop Navigation Bar (Only on Desktop) */}
          <div
            className="collapse navbar-collapse desktop-navbar-collapse"
            id="navbarTopMenu"
          >
            <ul className="navbar-nav mr-auto mt-2 mt-md-0">
              <li className={`nav-item dropdown has-submenu ${openSubmenus[1] ? "show" : ""}`}>
                <span
                  className={`nav-link dropdown-toggle ${
                    openSubmenus[1] ? "show" : ""
                  }`}
                  onClick={() => toggleDropdown(1)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleDropdown(1)}
                >
                  {t("nav.who_we_are")}
                </span>
                <ul className={`dropdown-menu ${openSubmenus[1] ? "show" : ""}`}>
                  <Menu
                    data={data}
                    onItemClick={closeMenu}
                    submenu="who"
                  />
                </ul>
              </li>

              <li className={`nav-item dropdown has-submenu ${openSubmenus[2] ? "show" : ""}`}>
                <span
                  className={`nav-link dropdown-toggle ${
                    openSubmenus[2] ? "show" : ""
                  }`}
                  onClick={() => toggleDropdown(2)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleDropdown(2)}
                >
                  {t("nav.publications")}
                </span>
                <ul className={`dropdown-menu ${openSubmenus[2] ? "show" : ""}`}>
                  <Menu
                    data={data}
                    onItemClick={closeMenu}
                    submenu="pub"
                  />
                </ul>
              </li>

              <li className={`nav-item dropdown has-submenu ${openSubmenus[3] ? "show" : ""}`}>
                <span
                  className={`nav-link dropdown-toggle ${
                    openSubmenus[3] ? "show" : ""
                  }`}
                  onClick={() => toggleDropdown(3)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleDropdown(3)}
                >
                  {t("nav.activity")}
                </span>
                <ul className={`dropdown-menu ${openSubmenus[3] ? "show" : ""}`}>
                  <Menu
                    data={data}
                    onItemClick={closeMenu}
                    submenu="act"
                  />
                </ul>
              </li>

              <li className={`nav-item dropdown has-submenu ${openSubmenus[4] ? "show" : ""}`}>
                <span
                  className={`nav-link dropdown-toggle ${
                    openSubmenus[4] ? "show" : ""
                  }`}
                  onClick={() => toggleDropdown(4)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleDropdown(4)}
                >
                  {t("nav.transparency")}
                </span>
                <ul className={`dropdown-menu ${openSubmenus[4] ? "show" : ""}`}>
                  <Menu
                    data={data}
                    onItemClick={closeMenu}
                    submenu="trasp"
                  />
                </ul>
              </li>

              <li className="nav-item">
                <Link
                  className="nav-link"
                  to="/contacts"
                  onClick={closeMenu}
                >
                  {t("nav.contacts")}
                </Link>
              </li>
            </ul>

            <div className="nav navbar-nav ml-auto">
              <div className="head-social">
                <a
                  href="https://it-it.facebook.com/orchestraferrucciobusoni"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Seguici su Facebook"
                  aria-label="Facebook"
                >
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
                <a
                  href="https://www.instagram.com/orchestra_busoni/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Seguici su Instagram"
                  aria-label="Instagram"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
              </div>
              <div className="menu-right">
                <div
                  className="lang-capsule"
                  role="group"
                  aria-label="Language selection"
                >
                  {languages.map((language) => {
                    const isActive = currentLang === language.code;
                    return (
                      <button
                        key={language.country_code}
                        type="button"
                        className={`lang-pill-btn ${isActive ? "active" : ""}`}
                        onClick={(e) => handleLanguageChange(language.code, e)}
                        aria-label={language.name}
                        aria-pressed={isActive}
                        title={language.name}
                      >
                        <FlagIcon code={language.code} />
                        <span className="lang-code-text">
                          {language.code.toUpperCase()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Full-Screen Multi-Screen Navigation System */}
          <div
            className={`mobile-fullscreen-menu ${
              isMobileMenuOpen ? "show" : ""
            }`}
            aria-hidden={!isMobileMenuOpen}
          >
            <div className="mobile-menu-content">
              {activeMobileSubmenu === null ? (
                /* MAIN SCREEN (Centered Horizontally & Vertically) */
                <div className="mobile-main-view">
                  <ul className="mobile-nav-list">
                    <li>
                      <Link
                        to="/"
                        className="mobile-nav-link"
                        onClick={closeMenu}
                      >
                        Homepage
                      </Link>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="mobile-nav-link has-sub"
                        onClick={() => setActiveMobileSubmenu("who")}
                      >
                        <span>{t("nav.who_we_are")}</span>
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="sub-arrow"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="mobile-nav-link has-sub"
                        onClick={() => setActiveMobileSubmenu("pub")}
                      >
                        <span>{t("nav.publications")}</span>
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="sub-arrow"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="mobile-nav-link has-sub"
                        onClick={() => setActiveMobileSubmenu("act")}
                      >
                        <span>{t("nav.activity")}</span>
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="sub-arrow"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="mobile-nav-link has-sub"
                        onClick={() => setActiveMobileSubmenu("trasp")}
                      >
                        <span>{t("nav.transparency")}</span>
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="sub-arrow"
                        />
                      </button>
                    </li>
                    <li>
                      <Link
                        to="/contacts"
                        className="mobile-nav-link"
                        onClick={closeMenu}
                      >
                        {t("nav.contacts")}
                      </Link>
                    </li>
                  </ul>

                  <div className="mobile-menu-footer">
                    <div className="mobile-social-row">
                      <a
                        href="https://it-it.facebook.com/orchestraferrucciobusoni"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Seguici su Facebook"
                        aria-label="Facebook"
                      >
                        <FontAwesomeIcon icon={faFacebook} />
                      </a>
                      <a
                        href="https://www.instagram.com/orchestra_busoni/"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Seguici su Instagram"
                        aria-label="Instagram"
                      >
                        <FontAwesomeIcon icon={faInstagram} />
                      </a>
                    </div>
                    <div
                      className="lang-capsule"
                      role="group"
                      aria-label="Selezione lingua"
                    >
                      {languages.map((language) => {
                        const isActive = currentLang === language.code;
                        return (
                          <button
                            key={language.country_code}
                            type="button"
                            className={`lang-pill-btn ${
                              isActive ? "active" : ""
                            }`}
                            onClick={(e) =>
                              handleLanguageChange(language.code, e)
                            }
                            aria-label={language.name}
                            aria-pressed={isActive}
                            title={language.name}
                          >
                            <FlagIcon code={language.code} />
                            <span className="lang-code-text">
                              {language.code.toUpperCase()}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* SUBMENU SCREEN (Centered Horizontally & Vertically) */
                <div className="mobile-subscreen-view">
                  <div className="mobile-subscreen-header">
                    <h2 className="mobile-subscreen-title">
                      {getSubmenuTitle(activeMobileSubmenu)}
                    </h2>
                    <div className="mobile-subscreen-divider"></div>
                  </div>

                  <ul className="mobile-subscreen-list">
                    {(data[activeMobileSubmenu] || []).map(
                      ({ name, to, id }, idx) => (
                        <li
                          key={id}
                          style={{
                            animationDelay: `${0.04 * (idx + 1)}s`,
                          }}
                        >
                          <Link
                            to={to}
                            className="mobile-subscreen-link"
                            onClick={closeMenu}
                          >
                            {t(`nav.${name}`)}
                          </Link>
                        </li>
                      )
                    )}
                  </ul>

                  <button
                    type="button"
                    className="mobile-subscreen-back"
                    onClick={() => setActiveMobileSubmenu(null)}
                    aria-label="Torna al menu principale"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>{isEnglish ? "Back" : "Indietro"}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
